import EvaluationService from 'services/data-handler-services/evaluation-service';
import utils from 'lib/utils';
import Attempt from 'ef/models/assessments/attempt';
import QuestionsCollection from 'ef/collections/questions_collection';
import EF from 'ef/ef';
import $ from 'jquery';
const AssessmentService = function () {
  this.savePartialAttempts = true;

  this.getRedirectUrlForCurrentPage = function () {
    const currentPage = EF.module.currentActivity.currentPage;
    return `#${currentPage.path()}?assessmentTaken=true`;
  };

  this.triggerAssessment = function (blockView) {
    const redirectUrl = this.getRedirectUrlForCurrentPage();
    const data = {
      event_type: 'assessment-initialized',
      redirectUrl: redirectUrl,
      slug: blockView.model.get('assessment_slug'),
    };
    /**
     * Vulnerability: (Cross-Site Scripting (XSS))
     * Authors should not use the wildcard keyword ( *) in the targetOrigin
     * argument in messages that contain any confidential information,
     * as otherwise there is no way to guarantee that the message is only
     * delivered to the recipient to which it was intended.
     */
    window.postMessage(data, window.location.origin);
  };

  this.userHasCompletedAssessment = function (courseSlug) {
    return EvaluationService.userHasPassedEvaluation(courseSlug).then(gradingStatus => gradingStatus);
  };
  this.shouldRetry = async function (evaluation = {}, courseAssessmentSlug) {
    const evaluationCompleted = await EvaluationService.userHasCompletedEvaluation(courseAssessmentSlug);
    return evaluationCompleted && evaluation.track_retakes;
  };
  this.getBestScore = function (courseSlug) {
    return EvaluationService.bestScore(courseSlug);
  };

  this.loadCourseAssessment = async function (view) {
    const assessmentSlug = view.model.get('assessment_slug');

    let { evaluation, responseSet } = await this.expandAndGetResponseSet(assessmentSlug);

    const shouldRetry = await this.shouldRetry(evaluation.get('evaluation'), assessmentSlug);

    if (shouldRetry) {
      await EvaluationService.handleMissingOrRetakeResponseSet(assessmentSlug, shouldRetry).catch(err =>
        console.error(err)
      );

      ({ evaluation, responseSet } = await this.expandAndGetResponseSet(assessmentSlug));
    }

    const sections = evaluation.get('sections');

    const questions = new QuestionsCollection(
      sections.reduce((result, section) => {
        return result.concat(section.questions);
      }, [])
    );

    // Foundry json workaround
    questions.each(function (q) {
      if (!q.has('response_type') && q.has('type')) {
        const foundryResponseType = {
          'Questions::MultipleResponse': 'multiple_selection',
          'Questions::MultipleChoice': 'single_selection',
        };
        q.set('response_type', foundryResponseType[q.get('type')]);
      }
    });

    view.assessment.set('questions', questions);
    view.assessment.set('original_questions', questions);
    view.assessment.set('response_set', responseSet);
    view.assessment.set('subtext', view.model.get('subtext'));
    let passing_grade = view.model.get('passing_grade');
    view.assessment.set('must_pass', view.model.get('must_pass'));
    if (view.assessment.get('must_pass')) {
      passing_grade = passing_grade ?? 100;
    }
    view.assessment.set('passing_grade', passing_grade);

    const attempt = new Attempt(1, {
      assessment: view.assessment,
    });

    attempt.set({
      id: responseSet.id,
      token: EvaluationService.findEvaluationTokenBySlug(view.model.get('assessment_slug')),
    });
    view.assessment.set('attempt', attempt);

    return view;
  };

  this.expandAndGetResponseSet = async function (assessmentSlug) {
    try {
      await EvaluationService.expandedResponseSet(assessmentSlug);
      const evaluation = await EvaluationService.findEvaluationBySlug(assessmentSlug);
      const responseSet = await EvaluationService.getResponseSetFromModel(assessmentSlug);
      return { evaluation, responseSet };
    } catch (e) {
      console.error(e);
    }
  };

  this.getUrl = function (assessment, isSaving) {
    const includes = isSaving ? '' : '?include=evaluation,sections.questions.answers';
    return `${assessment.get('response_set').endpoint}/response_sets/${
      assessment.get('response_set').id
    }.json${includes}`;
  };

  this.isAttemptCorrect = function (results, questionId) {
    if (Array.isArray(results.incorrect_answers)) {
      return !results.incorrect_answers.some(ia => ia.question_id === questionId);
    }
  };

  this.getAssessmentStatus = function (courseSlug) {
    const attempts = this.getResponseSetForSlug(courseSlug);
    if (attempts.length === 0 || attempts.every(attempt => attempt.get('status') === 'not_started')) {
      return 'not-attempted';
    }

    if (attempts.every(attempt => !attempt.get('passed'))) {
      return 'failed';
    }

    if (attempts.some(attempt => attempt.get('passed'))) {
      return 'passed';
    }
  };

  this.getResponseSetId = function (courseSlug, mapping) {
    const response = mapping[courseSlug];
    if (response) {
      return response;
    }

    console.warn('Invalid assessment mapping for slug: ', courseSlug); //eslint-disable-line no-console
    console.warn('Here are the configured assessment mappings for reference: ', mapping); //eslint-disable-line no-console
    return {};
    // trigger something special...
  };

  this.getResponseSetForSlug = function (courseSlug) {
    const mapping = EvaluationService.mapping || {};
    const response = this.getResponseSetId(courseSlug, mapping);

    return EF.responseSet.filter(function (rs) {
      return rs.get('id') === response.id;
    });
  };

  this.getActivityProgressForSlug = function () {};

  this.isAttemptComplete = function (attempt) {
    const answers = $.map(attempt.get('answers'), function (answer) {
      return String(answer.question_id);
    });

    const questions = attempt.assessment.get('original_questions') || attempt.assessment.get('questions');

    return questions.every(function (question) {
      return $.inArray(question.get('id'), answers) >= 0;
    });
  };

  this.isAttemptFinished = function (attempt) {
    return attempt.get('status') === 'completed';
  };

  this.parseAttempt = function (data) {
    if (data && data.data) {
      return utils.sanifyJsonApiObject(data.data, data.included);
    } else {
      return data;
    }
  };

  this.refreshActivityProgress = function () {};

  this.getAttemptJSON = function (thisAttempt) {
    const questions = thisAttempt.assessment.get('questions');

    const answers = thisAttempt.get('answers').map(ans => {
      const { answer_id, question_id } = ans;
      if (!answer_id || !question_id) {
        return;
      }
      return {
        value: answer_id.toString(),
        question_id: question_id,
        response_type: this.getResponseType(questions, question_id) ?? 'single_selection',
      };
    });

    return {
      data: {
        id: thisAttempt.get('id'),
        type: 'response_sets',
        attributes: {
          answers,
        },
      },
    };
  };

  this.getResponseType = function (questions, question_id) {
    const question = [...questions.models].find(question => Number(question.id) === Number(question_id));
    return question?.get('response_type');
  };
};

export default new AssessmentService();
